// The VR / AR entry in the modes row (Josh, 09-27: "I need the enter vr button visible"): on a Quest it enters VR,
// on Android Chrome it enters AR (the arc placed in the real room through the camera), and anywhere else — an iPhone,
// a Mac — a tap says which browsers can, since iOS has no WebXR session of any kind.
//
// The session is asked for here, inside the tap, before anything heavy loads: three.js and the room (core/render-gl.js)
// come only once a session has been granted, so Snap and Look never load them. Then `onSession(session, { ar })` hands
// it over; `onEnd()` runs when it ends. A refusal is read in the status line, since a phone has no console to open:
// the floor-anchored space first, then the plain "local" one; AR refused on Android falls back to VR. A long press on
// the entry tries the other kind of session (VR where AR is the default).
//
// The room draws dark glass whatever the page's Appearance (core/appearance.js `immersive`), as the headset does.
//
// One seam: `installXRButton({ logLine, say, onSession, onEnd })`.
import { immersive } from './appearance.js'

export function installXRButton({ logLine, say, onSession, onEnd }) {
  const button = document.createElement('a')
  button.href = '#xr'
  button.className = 'pill xr'
  button.textContent = 'VR'
  document.getElementById('modes').appendChild(button)
  let xrMode = null
  let session = null
  const label = () => { button.textContent = session ? (xrMode === 'immersive-ar' ? 'Stop AR' : 'Exit VR') : xrMode === 'immersive-ar' ? 'AR' : 'VR' }

  async function ask(mode) {
    logLine('requestSession ' + mode + ' {optionalFeatures: [local-floor, hand-tracking]}')
    try { return await navigator.xr.requestSession(mode, { optionalFeatures: ['local-floor', 'hand-tracking'] }) }
    catch (e1) {
      logLine('refused: ' + e1.name + ': ' + e1.message)
      logLine('requestSession ' + mode + ' with nothing at all')
      try { return await navigator.xr.requestSession(mode) }
      catch (e2) {
        logLine('refused: ' + e2.name + ': ' + e2.message)
        // AR refused outright on Android is ARCore missing (Google Play Services for AR) or a phone without it; VR is
        // usually still there, so take that and say why (Josh's phone, 09-27)
        if (mode === 'immersive-ar' && await navigator.xr.isSessionSupported('immersive-vr').catch(() => false)) {
          logLine('AR refused; trying immersive-vr instead')
          xrMode = 'immersive-vr'
          const s = await navigator.xr.requestSession('immersive-vr', { optionalFeatures: ['local-floor'] })
          say('AR is not available on this phone (it needs Google Play Services for AR); VR instead.')
          return s
        }
        throw e2
      }
    }
  }

  button.addEventListener('click', async (e) => {
    e.preventDefault()
    if (session) { session.end(); return }
    if (!xrMode) return say('This browser has no WebXR. Enter VR works in a Quest\'s browser and in Safari on visionOS; Enter AR in Chrome on Android. Here, Look is the way in.')
    try {
      session = await ask(xrMode)
      const granted = session.enabledFeatures || []
      logLine('session: blend ' + session.environmentBlendMode + '; features ' + (granted.join(',') || '(none listed)') + '; visibility ' + session.visibilityState)
      session.addEventListener('end', () => { session = null; immersive(false); label(); onEnd() })
      immersive(true)
      label()
      say('Loading the room…')
      await onSession(session, { ar: xrMode === 'immersive-ar' })
      say((xrMode === 'immersive-ar' ? 'AR' : 'VR') + (session?.environmentBlendMode ? ' (' + session.environmentBlendMode + ')' : '') + ': the arc is 2 m ahead at eye height — turn to it.' + (matchMedia('(pointer: coarse)').matches ? ' Tap a window to bring it up; tap beside it to put it back.' : ''))
    } catch (e) {
      try { session?.end() } catch {}
      session = null; immersive(false); label()
      logLine('failed: ' + (e.name || '') + ' ' + (e.message || e) + (e.stack ? ' | ' + String(e.stack).split('\n').slice(0, 2).join(' ') : ''))
      say('XR would not start: ' + (e.name ? e.name + ': ' : '') + (e.message || e) + ' — tap the build stamp for the log')
    }
  })
  if (navigator.xr) {
    navigator.xr.isSessionSupported('immersive-vr').then((ok) => { logLine('immersive-vr supported: ' + ok); if (ok && !xrMode) { xrMode = 'immersive-vr'; label() } }).catch((e) => logLine('immersive-vr check failed: ' + e.message))
    navigator.xr.isSessionSupported('immersive-ar').then((ok) => { logLine('immersive-ar supported: ' + ok); if (ok) { xrMode = 'immersive-ar'; label() } }).catch((e) => logLine('immersive-ar check failed: ' + e.message))
    let press = null
    button.addEventListener('pointerdown', () => { press = setTimeout(() => { press = null; xrMode = xrMode === 'immersive-ar' ? 'immersive-vr' : 'immersive-ar'; label(); logLine('switched to ' + xrMode + ' (long press)') }, 700) })
    button.addEventListener('pointerup', () => { clearTimeout(press) })
  }
  return { get presenting() { return !!session } }
}
